import type { Edge } from '@xyflow/react';

import type { OrgMvpNode } from './organogramaTypes';

export type OrganogramaTemplate = {
  key: string;
  nome: string;
  descricao: string;
  nodes: OrgMvpNode[];
  edges: Edge[];
};

const card = (
  id: string,
  x: number,
  y: number,
  data: {
    kind: 'person' | 'department';
    label: string;
    subtitle: string;
    description?: string;
    color: string;
    figureKey: string;
  },
  size: { width: number; height: number } = { width: 220, height: 118 },
): OrgMvpNode => ({
  id,
  type: 'card',
  position: { x, y },
  width: size.width,
  height: size.height,
  zIndex: 10,
  data: {
    kind: data.kind,
    label: data.label,
    subtitle: data.subtitle,
    description: data.description || '',
    sourceType: data.kind === 'person' ? 'ORG_ONLY' : undefined,
    usuarioId: null,
    departamentoId: null,
    color: data.color,
    textColor: '#0F172A',
    figure: { type: 'catalog', key: data.figureKey },
  },
  style: { width: size.width, height: size.height },
} as OrgMvpNode);

const column = (id: string, x: number, title: string, color: string): OrgMvpNode => ({
  id,
  type: 'column',
  position: { x, y: 40 },
  width: 340,
  height: 620,
  zIndex: 0,
  data: { title, color, orientation: 'vertical' },
  style: { width: 340, height: 620 },
} as OrgMvpNode);

const link = (id: string, source: string, target: string, stroke: string, sourceHandle: string, targetHandle: string): Edge => ({
  id,
  source,
  target,
  sourceHandle,
  targetHandle,
  type: 'smoothstep',
  animated: false,
  style: { stroke, strokeWidth: 2.2 },
  data: { tipo: 'direta' },
});

// Linha tracejada — usada nos modelos pra indicar vínculo indireto/matricial, sem sobrepor
// visualmente a hierarquia sólida (reporte direto) que os cards já desenham.
const linkPontilhada = (id: string, source: string, target: string, stroke: string, sourceHandle: string, targetHandle: string): Edge => ({
  id,
  source,
  target,
  sourceHandle,
  targetHandle,
  type: 'smoothstep',
  animated: false,
  style: { stroke, strokeWidth: 2, strokeDasharray: '6 4' },
  data: { tipo: 'indireta' },
});

export const organogramaTemplates: OrganogramaTemplate[] = [
  {
    key: 'hierarquia-classica',
    nome: 'Hierarquia clássica',
    descricao: 'Direção no topo e três áreas subordinadas — o formato mais comum de organograma.',
    nodes: [
      card('ceo', 420, 80, { kind: 'person', label: 'Diretoria', subtitle: 'Direção executiva', color: '#2563EB', figureKey: 'executive' }),
      card('operacoes', 160, 320, { kind: 'department', label: 'Operações', subtitle: 'Produção e logística', color: '#059669', figureKey: 'factory' }),
      card('administrativo', 420, 320, { kind: 'department', label: 'Administrativo', subtitle: 'Financeiro e RH', color: '#7C3AED', figureKey: 'admin' }),
      card('comercial', 680, 320, { kind: 'department', label: 'Comercial', subtitle: 'Vendas e marketing', color: '#EA580C', figureKey: 'sales' }),
    ],
    edges: [
      link('e-ceo-operacoes', 'ceo', 'operacoes', '#2563EB', 'bottom', 't-top'),
      link('e-ceo-administrativo', 'ceo', 'administrativo', '#2563EB', 'bottom', 't-top'),
      link('e-ceo-comercial', 'ceo', 'comercial', '#2563EB', 'bottom', 't-top'),
    ],
  },
  {
    key: 'colunas-por-nivel',
    nome: 'Colunas por nível',
    descricao: 'Diretoria, gestão e operação separadas em colunas — bom para times grandes.',
    nodes: [
      column('col-diretoria', 40, 'Diretoria', '#DBEAFE'),
      column('col-gestao', 400, 'Gestão', '#DCFCE7'),
      column('col-operacao', 760, 'Operação', '#FFEDD5'),
      card('dir-geral', 95, 150, { kind: 'person', label: 'Direção geral', subtitle: 'Estratégia', color: '#2563EB', figureKey: 'executive' }, { width: 210, height: 118 }),
      card('gestao-produto', 455, 150, { kind: 'department', label: 'Produto', subtitle: 'Planejamento', color: '#059669', figureKey: 'building' }),
      card('gestao-pessoas', 455, 320, { kind: 'department', label: 'Pessoas', subtitle: 'RH e cultura', color: '#059669', figureKey: 'hr' }),
      card('op-engenharia', 815, 150, { kind: 'department', label: 'Engenharia', subtitle: 'Times técnicos', color: '#EA580C', figureKey: 'tools' }),
      card('op-suporte', 815, 320, { kind: 'department', label: 'Suporte', subtitle: 'Atendimento', color: '#EA580C', figureKey: 'support' }),
    ],
    edges: [
      link('e-dir-produto', 'dir-geral', 'gestao-produto', '#2563EB', 'right', 't-left'),
      link('e-dir-pessoas', 'dir-geral', 'gestao-pessoas', '#2563EB', 'right', 't-left'),
      link('e-produto-engenharia', 'gestao-produto', 'op-engenharia', '#059669', 'right', 't-left'),
      link('e-produto-suporte', 'gestao-produto', 'op-suporte', '#059669', 'right', 't-left'),
    ],
  },
  {
    key: 'matricial-funcional-projeto',
    nome: 'Matricial (funcional x projeto)',
    descricao: 'Áreas funcionais fixas com linhas tracejadas pra projetos que cruzam mais de uma área.',
    nodes: [
      card('diretoria-m', 420, 60, { kind: 'person', label: 'Diretoria', subtitle: 'Direção executiva', color: '#2563EB', figureKey: 'executive' }),
      card('eng-m', 140, 280, { kind: 'department', label: 'Engenharia', subtitle: 'Times técnicos', color: '#059669', figureKey: 'dev' }),
      card('produto-m', 420, 280, { kind: 'department', label: 'Produto', subtitle: 'Planejamento', color: '#059669', figureKey: 'building' }),
      card('design-m', 700, 280, { kind: 'department', label: 'Design', subtitle: 'UX e criação', color: '#059669', figureKey: 'design' }),
      card('proj-alpha', 140, 500, { kind: 'department', label: 'Projeto Alpha', subtitle: 'Iniciativa cross-área', color: '#EA580C', figureKey: 'startup' }, { width: 220, height: 100 }),
      card('proj-beta', 700, 500, { kind: 'department', label: 'Projeto Beta', subtitle: 'Iniciativa cross-área', color: '#EA580C', figureKey: 'startup' }, { width: 220, height: 100 }),
    ],
    edges: [
      link('e-dir-eng-m', 'diretoria-m', 'eng-m', '#2563EB', 'bottom', 't-top'),
      link('e-dir-produto-m', 'diretoria-m', 'produto-m', '#2563EB', 'bottom', 't-top'),
      link('e-dir-design-m', 'diretoria-m', 'design-m', '#2563EB', 'bottom', 't-top'),
      linkPontilhada('e-eng-alpha', 'eng-m', 'proj-alpha', '#EA580C', 'bottom', 't-top'),
      linkPontilhada('e-produto-alpha', 'produto-m', 'proj-alpha', '#EA580C', 'left', 't-right'),
      linkPontilhada('e-produto-beta', 'produto-m', 'proj-beta', '#EA580C', 'right', 't-left'),
      linkPontilhada('e-design-beta', 'design-m', 'proj-beta', '#EA580C', 'bottom', 't-top'),
    ],
  },
  {
    key: 'rede-de-unidades',
    nome: 'Rede de unidades/filiais',
    descricao: 'Matriz no topo com filiais/unidades regionais abaixo — bom pra empresas com mais de um endereço.',
    nodes: [
      card('matriz-u', 420, 60, { kind: 'person', label: 'Matriz', subtitle: 'Direção geral', color: '#2563EB', figureKey: 'home' }),
      card('filial-1', 140, 280, { kind: 'department', label: 'Filial — Unidade 1', subtitle: 'Gestor local', color: '#0891B2', figureKey: 'location' }),
      card('filial-2', 420, 280, { kind: 'department', label: 'Filial — Unidade 2', subtitle: 'Gestor local', color: '#0891B2', figureKey: 'location' }),
      card('filial-3', 700, 280, { kind: 'department', label: 'Filial — Unidade 3', subtitle: 'Gestor local', color: '#0891B2', figureKey: 'location' }),
    ],
    edges: [
      link('e-matriz-filial1', 'matriz-u', 'filial-1', '#2563EB', 'bottom', 't-top'),
      link('e-matriz-filial2', 'matriz-u', 'filial-2', '#2563EB', 'bottom', 't-top'),
      link('e-matriz-filial3', 'matriz-u', 'filial-3', '#2563EB', 'bottom', 't-top'),
    ],
  },
  {
    key: 'conselho-diretoria',
    nome: 'Conselho e diretoria executiva',
    descricao: 'Conselho de Administração acima do CEO, com a diretoria executiva abaixo — formato de empresas com governança formal.',
    nodes: [
      card('conselho-c', 420, 40, { kind: 'department', label: 'Conselho de Administração', subtitle: 'Governança', color: '#1E293B', figureKey: 'award' }, { width: 260, height: 100 }),
      card('ceo-c', 420, 200, { kind: 'person', label: 'CEO', subtitle: 'Diretor executivo', color: '#2563EB', figureKey: 'executive' }),
      card('cfo-c', 140, 420, { kind: 'department', label: 'Diretoria Financeira', subtitle: 'CFO', color: '#7C3AED', figureKey: 'finance' }),
      card('coo-c', 420, 420, { kind: 'department', label: 'Diretoria de Operações', subtitle: 'COO', color: '#7C3AED', figureKey: 'factory' }),
      card('cmo-c', 700, 420, { kind: 'department', label: 'Diretoria Comercial', subtitle: 'CMO', color: '#7C3AED', figureKey: 'sales' }),
    ],
    edges: [
      link('e-conselho-ceo', 'conselho-c', 'ceo-c', '#1E293B', 'bottom', 't-top'),
      link('e-ceo-cfo', 'ceo-c', 'cfo-c', '#2563EB', 'bottom', 't-top'),
      link('e-ceo-coo', 'ceo-c', 'coo-c', '#2563EB', 'bottom', 't-top'),
      link('e-ceo-cmo', 'ceo-c', 'cmo-c', '#2563EB', 'bottom', 't-top'),
    ],
  },
];
